import fs from "fs";
import path from "path";
import { CiReport } from "../models/ciReport.js";
import { CompatibilityIssue } from "../analyze/models.js";

/**
 * SARIF severity mapping.
 *
 * error   → error
 * warning → warning
 * info    → note
 */
function mapSeverity(
  severity: CompatibilityIssue["severity"]
): "error" | "warning" | "note" {
  switch (severity) {
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
    default:
      return "note";
  }
}

/**
 * Generates a SARIF rule ID that is stable across runs:
 * Format: `RN<SEVERITY_PREFIX><PACKAGE_SLUG>`
 * e.g. react-native + warning → RNW-react-native
 */
function sarifRuleId(issue: CompatibilityIssue): string {
  const prefix = issue.severity === "error" ? "E" : issue.severity === "warning" ? "W" : "I";
  const slug = issue.packageName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `RN${prefix}-${slug}`;
}

interface SarifRule {
  id: string;
  name: string;
  shortDescription: { text: string };
  helpUri?: string;
  properties: { severity: string };
}

interface SarifResult {
  ruleId: string;
  level: "error" | "warning" | "note";
  message: { text: string };
  locations: { physicalLocation: { artifactLocation: { uri: string } } }[];
}

export interface SarifReport {
  $schema: string;
  version: string;
  runs: {
    tool: {
      driver: {
        name: string;
        version: string;
        informationUri: string;
        rules: SarifRule[];
      };
    };
    results: SarifResult[];
    artifacts: { location: { uri: string } }[];
  }[];
}

/**
 * Converts a CiReport into a SARIF v2.1.0 compliant report object.
 */
export function buildSarifReport(report: CiReport, toolVersion = "0.13.0"): SarifReport {
  // De-duplicate rules by ruleId
  const ruleMap = new Map<string, SarifRule>();
  const results: SarifResult[] = [];

  for (const issue of report.issues) {
    const ruleId = sarifRuleId(issue);
    const level = mapSeverity(issue.severity);

    if (!ruleMap.has(ruleId)) {
      ruleMap.set(ruleId, {
        id: ruleId,
        name: `${issue.packageName} ${issue.severity}`,
        shortDescription: { text: issue.message },
        helpUri: issue.recommendation ? undefined : undefined,
        properties: { severity: issue.severity },
      });
    }

    const result: SarifResult = {
      ruleId,
      level,
      message: {
        text: issue.recommendation
          ? `${issue.message} — ${issue.recommendation}`
          : issue.message,
      },
      locations: [
        {
          physicalLocation: {
            artifactLocation: { uri: "package.json" },
          },
        },
      ],
    };

    results.push(result);
  }

  const rules = Array.from(ruleMap.values()).sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  return {
    $schema:
      "https://raw.githubusercontent.com/oasis-tcs/sarif-spec/master/Schemata/sarif-schema-2.1.0.json",
    version: "2.1.0",
    runs: [
      {
        tool: {
          driver: {
            name: "Kyro",
            version: toolVersion,
            informationUri: "https://github.com/kyro/kyro",
            rules,
          },
        },
        results,
        artifacts: [{ location: { uri: "package.json" } }],
      },
    ],
  };
}

/**
 * Writes a SARIF report to `outputPath`.
 * Creates parent directories if needed.
 */
export function writeSarifReport(sarifReport: SarifReport, outputPath: string): void {
  const dir = path.dirname(outputPath);
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  fs.writeFileSync(outputPath, JSON.stringify(sarifReport, null, 2), "utf-8");
}
